import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import ModuleHeader, { ModuleHeaderBack } from "../../components/ModuleHeader/ModuleHeader";
import {
  createMerchantOwner,
  deleteMerchantOwner,
  getMerchantOwner,
  getMerchantOwnerMeta,
  updateMerchantOwner,
} from "../../api/merchantOwnerApi";
import "./merchant-owner-form.scss";

const emptyForm = {
  name: "",
  phone: "",
  status: "1",
  password: "",
  passwordConfirmation: "",
  merchantIds: [],
};

const MerchantOwnerFormPage = () => {
  const navigate = useNavigate();
  const { ownerId } = useParams();
  const isEdit = Boolean(ownerId);
  const [form, setForm] = useState(emptyForm);
  const [merchantOptions, setMerchantOptions] = useState([]);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [merchantSearch, setMerchantSearch] = useState("");
  const [merchantDropdownOpen, setMerchantDropdownOpen] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setMessage("");
      try {
        const [meta, owner] = await Promise.all([
          getMerchantOwnerMeta(),
          isEdit ? getMerchantOwner(ownerId) : Promise.resolve(null),
        ]);
        setMerchantOptions(meta.merchants || []);
        if (owner) {
          setForm({
            ...emptyForm,
            name: owner.name || "",
            phone: owner.phone || "",
            status: String(owner.status),
            merchantIds: (owner.merchant_ids || []).map(String),
          });
        }
      } catch (requestError) {
        setMessage(requestError.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [isEdit, ownerId]);

  const updateField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    setMessage("");
  };

  const toggleMerchant = (merchantId) => {
    const id = String(merchantId);
    setForm((current) => ({
      ...current,
      merchantIds: current.merchantIds.includes(id)
        ? current.merchantIds.filter((value) => value !== id)
        : [...current.merchantIds, id],
    }));
  };

  const filteredMerchants = useMemo(() => {
    const query = merchantSearch.trim().toLowerCase();
    return query
      ? merchantOptions.filter((merchant) => merchant.name.toLowerCase().includes(query))
      : merchantOptions;
  }, [merchantOptions, merchantSearch]);

  const selectedMerchants = merchantOptions.filter((merchant) =>
    form.merchantIds.includes(String(merchant.id))
  );

  const submit = async (event) => {
    event.preventDefault();
    if (!form.merchantIds.length) {
      setMessage("Select at least one merchant.");
      return;
    }
    if (form.password !== form.passwordConfirmation) {
      setMessage("The password confirmation does not match.");
      return;
    }

    setSaving(true);
    setMessage("");
    const payload = {
      name: form.name.trim(),
      phone: form.phone,
      status: Number(form.status),
      merchant_ids: form.merchantIds.map(Number),
      password: form.password || undefined,
      password_confirmation: form.passwordConfirmation || undefined,
    };

    try {
      if (isEdit) {
        await updateMerchantOwner(ownerId, payload);
      } else {
        await createMerchantOwner(payload);
      }
      navigate("/merchant-owners", { replace: true });
    } catch (requestError) {
      setMessage(requestError.message);
    } finally {
      setSaving(false);
    }
  };

  const removeOwner = async () => {
    if (!window.confirm("Delete this Merchant Owner? Their merchant access will be removed.")) return;
    setSaving(true);
    setMessage("");
    try {
      await deleteMerchantOwner(ownerId);
      navigate("/merchant-owners", { replace: true });
    } catch (requestError) {
      setMessage(requestError.message);
      setSaving(false);
    }
  };

  return (
    <main className="merchant-owner-form">
      <ModuleHeader
        bleed
        title={isEdit ? "Manage Merchant Owner" : "Add Merchant Owner"}
        description="Assign one owner account to one or more merchants."
        back={(
          <ModuleHeaderBack type="button" onClick={() => navigate("/merchant-owners")}>
            ← Back to Merchant Owners
          </ModuleHeaderBack>
        )}
      />

      <form className="merchant-owner-form__card" onSubmit={submit}>
        <header>
          <div>
            <h3>Owner Details</h3>
            <p>Phone number is unique system-wide and is used as the login identifier.</p>
          </div>
          <span>Owner</span>
        </header>

        <div className="merchant-owner-form__grid">
          <label>
            <span>Full Name</span>
            <input value={form.name} onChange={(event) => updateField("name", event.target.value)} required disabled={loading} />
          </label>
          <label className="merchant-owner-form__phone-field">
            <span>Login Phone Number</span>
            <PhoneInput
              country="my"
              value={form.phone}
              onChange={(value) => updateField("phone", value)}
              preferredCountries={["my", "sg", "bn", "id"]}
              enableSearch
              countryCodeEditable={false}
              disableSearchIcon
              inputProps={{ name: "phone", required: true, autoComplete: "tel", disabled: loading }}
            />
            <small>Select the country dial code and enter the mobile number used for login.</small>
          </label>
          <label>
            <span>Role</span>
            <input value="Merchant Owner" disabled />
          </label>
          <label>
            <span>Status</span>
            <select value={form.status} onChange={(event) => updateField("status", event.target.value)} disabled={loading}>
              <option value="1">Active</option>
              <option value="0">Inactive</option>
            </select>
          </label>
        </div>

        <section className="merchant-owner-form__merchant-access">
          <div>
            <h4>Merchant Access</h4>
            <p>Select one or more merchants. The owner automatically receives access to all outlets under each selected merchant.</p>
          </div>
          <div className="merchant-owner-form__merchant-select">
            <button type="button" className="merchant-owner-form__merchant-select-trigger" onClick={() => setMerchantDropdownOpen((value) => !value)} disabled={loading}>
              <span>{selectedMerchants.length ? `${selectedMerchants.length} merchant${selectedMerchants.length === 1 ? "" : "s"} selected` : "Select merchants"}</span>
              <span aria-hidden="true">{merchantDropdownOpen ? "⌃" : "⌄"}</span>
            </button>
            {merchantDropdownOpen && (
              <div className="merchant-owner-form__merchant-dropdown">
                <label className="merchant-owner-form__merchant-search">
                  <span className="sr-only">Search merchants</span>
                  <input type="search" value={merchantSearch} onChange={(event) => setMerchantSearch(event.target.value)} placeholder="Search merchant name..." autoFocus />
                </label>
                <div className="merchant-owner-form__merchant-dropdown-list">
                  {filteredMerchants.map((merchant) => {
                    const selected = form.merchantIds.includes(String(merchant.id));
                    return (
                      <label className={`merchant-owner-form__merchant-dropdown-option${selected ? " merchant-owner-form__merchant-dropdown-option--selected" : ""}`} key={merchant.id}>
                        <input type="checkbox" checked={selected} onChange={() => toggleMerchant(merchant.id)} />
                        <span>{merchant.name}</span>
                        <small>{selected ? "Assigned" : "Not assigned"}</small>
                      </label>
                    );
                  })}
                  {!filteredMerchants.length && <div className="merchant-owner-form__merchant-empty">No merchants found.</div>}
                </div>
              </div>
            )}
          </div>
          {selectedMerchants.length > 0 && (
            <div className="merchant-owner-form__merchant-selected">
              {selectedMerchants.map((merchant) => (
                <span key={merchant.id}>{merchant.name}<button type="button" onClick={() => toggleMerchant(merchant.id)} aria-label={`Remove ${merchant.name}`}>×</button></span>
              ))}
            </div>
          )}
        </section>

        <div className="merchant-owner-form__grid">
          <label className="merchant-owner-form__password-field">
            <span>{isEdit ? "New Password" : "Password"}</span>
            <input type="password" value={form.password} onChange={(event) => updateField("password", event.target.value)} minLength={8} required={!isEdit} />
            {isEdit && <small>Leave blank to keep the current password.</small>}
          </label>
          <label className="merchant-owner-form__password-field">
            <span>Confirm Password</span>
            <input type="password" value={form.passwordConfirmation} onChange={(event) => updateField("passwordConfirmation", event.target.value)} minLength={8} required={!isEdit || Boolean(form.password)} />
          </label>
        </div>

        {message && <p className="merchant-owner-form__message">{message}</p>}
        <footer>
          {isEdit && <button type="button" onClick={removeOwner} disabled={saving}>Delete Owner</button>}
          <button type="button" onClick={() => navigate("/merchant-owners")} disabled={saving}>Cancel</button>
          <button type="submit" disabled={loading || saving}>{saving ? "Saving…" : isEdit ? "Update Owner" : "Create Owner"}</button>
        </footer>
      </form>
    </main>
  );
};

export default MerchantOwnerFormPage;
